import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { Strophe, $pres, $msg, $iq } from 'strophe.js';
import { useAuth } from '../../contexts/AuthContext';
import { useXMPP } from '../../contexts/XMPPContext';
import { useRightRail } from '../../contexts/RightRailContext';
import { apiCall, ENDPOINTS } from '../../config/api';
import { FiArrowLeft, FiSend, FiRefreshCw, FiWifi, FiWifiOff, FiPaperclip } from 'react-icons/fi';
import ChatMediaViewer from './ChatMediaViewer';
import AuthImage from './AuthImage';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Contact {
  jid: string;
  name: string;
  presence: 'online' | 'away' | 'dnd' | 'offline';
}

interface Message {
  id: string;
  fromJid: string;
  body: string;
  timestamp: Date;
  outgoing: boolean;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentMime?: string;
}

type ConnStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'disconnected';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const bareJid = (jid: string) => jid.split('/')[0].toLowerCase();

const toPresence = (type: string | null, show: string | null): Contact['presence'] => {
  if (type === 'unavailable') return 'offline';
  if (show === 'away' || show === 'xa') return 'away';
  if (show === 'dnd') return 'dnd';
  return 'online';
};

const PRESENCE_DOT: Record<string, string> = {
  online:  'bg-success',
  away:    'bg-warning',
  dnd:     'bg-error',
  offline: 'bg-base-300',
};

const PRESENCE_ORDER = { online: 0, away: 1, dnd: 2, offline: 3 };

// ─── Component ───────────────────────────────────────────────────────────────

const ClinicalChatPanel: React.FC = () => {
  const { token } = useAuth();
  const { setStropheConn, status: xmppStatus } = useXMPP();
  const { registerButton, updateButton, unregisterButton } = useRightRail();

  const [connStatus, setConnStatus] = useState<ConnStatus>('idle');
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [contacts, setContacts]     = useState<Contact[]>([]);
  const [activeContact, setActiveContact] = useState<Contact | null>(null);
  const [messages, setMessages]     = useState<Record<string, Message[]>>({});
  const [unread, setUnread]         = useState<Record<string, number>>({});
  const [inputText, setInputText]   = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [viewer, setViewer]         = useState<{ url: string; name: string; mime: string } | null>(null);
  const [ctxMenu, setCtxMenu]       = useState<{ x: number; y: number; url: string; name: string; mime: string } | null>(null);

  const connRef            = useRef<any>(null);
  const myJidRef           = useRef('');
  const activeContactRef   = useRef<Contact | null>(null);
  const messagesEndRef     = useRef<HTMLDivElement>(null);
  const pendingPresenceRef = useRef<Record<string, Contact['presence']>>({});
  const dragCounter        = useRef(0);

  // Keep refs in sync so XMPP handlers avoid stale closures
  useEffect(() => { activeContactRef.current = activeContact; }, [activeContact]);
  // connectRef is initialised with a no-op; synced to the real connect after it is defined
  const connectRef = useRef<() => void>(() => {});

  // Dismiss context menu on any click outside it
  useEffect(() => {
    if (!ctxMenu) return;
    const dismiss = () => setCtxMenu(null);
    window.addEventListener('click', dismiss);
    return () => window.removeEventListener('click', dismiss);
  }, [ctxMenu]);

  // Register chat button in the right rail
  useEffect(() => {
    registerButton({
      id: 'chat',
      icon: <ChatBubbleLeftRightIcon className="h-5 w-5" />,
      label: 'Clinical Chat',
      order: 10,
    });
    return () => unregisterButton('chat');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep unread badge in sync
  useEffect(() => {
    updateButton('chat', { badge: unreadTotal });
  }, [unreadTotal]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── XMPP connection ──────────────────────────────────────────────────

  const connect = useCallback(async () => {
    if (!token) return;
    connRef.current?.disconnect();
    setConnStatus('connecting');
    setContacts([]);

    try {
      console.log('[ClinicalChat] Step 1: Fetching XMPP credentials from prebind...');
      const resp = await apiCall(ENDPOINTS.XMPP.PREBIND, { method: 'POST' }, token);
      console.log(`[ClinicalChat] Step 2: Prebind response status: ${resp.status}`);
      if (!resp.ok) throw new Error(`XMPP credentials failed: HTTP ${resp.status}`);
      const creds = await resp.json();
      console.log('[ClinicalChat] Step 3: Got credentials:', { jid: creds.jid, bosh_url: creds.bosh_url, has_password: !!creds.password });
      myJidRef.current = creds.jid;

      const boshUrl = creds.bosh_url;
      console.log(`[ClinicalChat] Step 4: Creating Strophe.Connection to ${boshUrl}`);

      const conn = new Strophe.Connection(boshUrl);
      // rawInput/rawOutput are instance properties in Strophe v3
      (conn as any).rawInput  = (data: string) => console.log('[ClinicalChat] RECV:', data);
      (conn as any).rawOutput = (data: string) => console.log('[ClinicalChat] SEND:', data);
      connRef.current = conn;

      console.log(`[ClinicalChat] Step 5: Calling conn.connect(${creds.jid}, <password>)`);
      conn.connect(creds.jid, creds.password, (status: number) => {
        const statusName = Object.keys(Strophe.Status).find(k => (Strophe.Status as any)[k] === status) ?? status;
        console.log(`[ClinicalChat] Step 6: Strophe status callback → ${statusName} (${status})`);

        if (status === Strophe.Status.CONNECTED) {
          setConnStatus('connected');
          setStropheConn(conn);

          // Auto-accept incoming presence subscription requests (mutual subscribe)
          conn.addHandler((pres: Element) => {
            const from = pres.getAttribute('from');
            if (from) {
              conn.send($pres({ type: 'subscribed', to: from }));
              conn.send($pres({ type: 'subscribe',   to: bareJid(from) }));
            }
            return true;
          }, null, 'presence', 'subscribe');

          // Presence updates — buffer early arrivals before contacts list is loaded
          const CONTROL_TYPES = new Set(['subscribe', 'subscribed', 'unsubscribe', 'unsubscribed']);
          conn.addHandler((pres: Element) => {
            const type = pres.getAttribute('type');
            if (CONTROL_TYPES.has(type ?? '')) return true;
            const from = bareJid(pres.getAttribute('from') || '');
            if (!from || from === bareJid(myJidRef.current)) return true;
            const showEl = pres.querySelector('show');
            const presence = toPresence(type, showEl?.textContent || null);
            setContacts(prev => {
              if (prev.length === 0) {
                // Contacts not loaded yet — buffer so we apply it once they arrive
                pendingPresenceRef.current[from] = presence;
                return prev;
              }
              return prev.map(c => (bareJid(c.jid) === from ? { ...c, presence } : c));
            });
            return true;
          }, null, 'presence');

          // Fetch contact list from Django, apply any buffered presence, then subscribe
          const myBare = bareJid(creds.jid);
          pendingPresenceRef.current = {};
          apiCall(ENDPOINTS.USERS.LIST, { method: 'GET' }, token!).then(async r => {
            if (!r.ok) return;
            const data = await r.json();
            const XMPP_DOMAIN = creds.jid.split('@')[1] ?? 'chat.local';
            const pending = pendingPresenceRef.current;
            const userList: { username: string; first_name: string; last_name: string; is_active: boolean }[] =
              Array.isArray(data) ? data : (data.results ?? []);
            const loaded = userList
              .filter(u => u.is_active && `${u.username}@${XMPP_DOMAIN}` !== myBare)
              .map(u => {
                const jid = `${u.username}@${XMPP_DOMAIN}`;
                return {
                  jid,
                  name:     [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username,
                  presence: (pending[bareJid(jid)] ?? 'offline') as Contact['presence'],
                };
              });
            pendingPresenceRef.current = {};
            setContacts(loaded);
            // Request presence subscription from every contact
            loaded.forEach(c => conn.send($pres({ type: 'subscribe', to: c.jid })));
          }).catch(() => {});

          // Incoming messages
          conn.addHandler((msg: Element) => {
            const from = bareJid(msg.getAttribute('from') || '');
            const bodyEl = msg.querySelector('body');
            if (!bodyEl?.textContent) return true;
            const newMsg: Message = {
              id:        msg.getAttribute('id') || `${Date.now()}`,
              fromJid:   from,
              body:      bodyEl.textContent,
              timestamp: new Date(),
              outgoing:  false,
            };
            const fromUsername = from.split('@')[0];
            setMessages(prev => {
              const existing = prev[from] || [];
              if (existing.some(m => m.id === newMsg.id)) return prev;
              return { ...prev, [from]: [...existing, newMsg] };
            });
            saveMessageToDB(newMsg, fromUsername, false);
            // Mark unread if this contact isn't currently open
            if (bareJid(activeContactRef.current?.jid || '') !== from) {
              setUnread(prev => {
                const next = { ...prev, [from]: (prev[from] || 0) + 1 };
                setUnreadTotal(Object.values(next).reduce((a, b) => a + b, 0));
                return next;
              });
            }
            return true;
          }, null, 'message', 'chat');

        } else if (
          status === Strophe.Status.CONNFAIL ||
          status === Strophe.Status.AUTHFAIL
        ) {
          setConnStatus('error');
        } else if (status === Strophe.Status.DISCONNECTED) {
          setStropheConn(null);
          setConnStatus(prev => {
            if (prev === 'connected') {
              // Auto-reconnect after a brief pause when an established connection drops
              setTimeout(() => connectRef.current(), 3000);
              return 'connecting';
            }
            return prev === 'connecting' ? 'error' : prev;
          });
        }
      });
    } catch (err) {
      console.error('[ClinicalChat] Connection error:', err);
      setConnStatus('error');
    }
  }, [token]);

  // Keep connectRef pointing at the latest connect so auto-reconnect never uses a stale closure
  useEffect(() => { connectRef.current = connect; }, [connect]);

  useEffect(() => {
    connect();
    return () => { connRef.current?.disconnect(); };
  }, [connect]);

  // Scroll to bottom whenever active chat gains a new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeContact]);

  // ─── Persistence helpers ──────────────────────────────────────────────

  const saveMessageToDB = useCallback((msg: Message, toUsername: string, outgoing: boolean) => {
    if (!token) return;
    apiCall(ENDPOINTS.CHAT.SAVE, {
      method: 'POST',
      body: JSON.stringify({
        id:           msg.id,
        to:           toUsername,
        body:         msg.body,
        timestamp:    msg.timestamp.toISOString(),
        outgoing,
        message_type: msg.attachmentMime?.startsWith('image/') ? 'image' : msg.attachmentUrl ? 'file' : 'text',
      }),
    }, token).catch(() => {});
  }, [token]);

  const loadHistory = useCallback(async (contactUsername: string) => {
    if (!token) return;
    try {
      const r = await apiCall(`${ENDPOINTS.CHAT.MESSAGES}?contact=${encodeURIComponent(contactUsername)}`, { method: 'GET' }, token);
      if (!r.ok) return;
      const data = await r.json();
      const myBare = bareJid(myJidRef.current);
      const loaded: Message[] = (data.messages as {
        id: string; from: string; to: string; body: string; timestamp: string; outgoing: boolean;
        attachment_url?: string; attachment_name?: string; attachment_mime?: string;
      }[]).map(m => ({
        id:             m.id,
        fromJid:        m.outgoing ? myBare : `${m.from}@chat.local`,
        body:           m.body,
        timestamp:      new Date(m.timestamp),
        outgoing:       m.outgoing,
        attachmentUrl:  m.attachment_url || undefined,
        attachmentName: m.attachment_name || undefined,
        attachmentMime: m.attachment_mime || undefined,
      }));
      setMessages(prev => {
        const key = contactUsername + '@chat.local';
        const bareKey = bareJid(key);
        const existing = prev[bareKey] || [];
        const existingIds = new Set(existing.map(m => m.id));
        const merged = [...loaded.filter(m => !existingIds.has(m.id)), ...existing]
          .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        return { ...prev, [bareKey]: merged };
      });
    } catch { /* silent */ }
  }, [token]);

  // ─── Actions ──────────────────────────────────────────────────────────

  const openChat = (contact: Contact) => {
    setActiveContact(contact);
    setUnread(prev => {
      const next = { ...prev, [bareJid(contact.jid)]: 0 };
      setUnreadTotal(Object.values(next).reduce((a, b) => a + b, 0));
      return next;
    });
    const username = bareJid(contact.jid).split('@')[0];
    loadHistory(username);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const sendMessage = () => {
    if (!inputText.trim() || !activeContact || !connRef.current) return;
    const body = inputText.trim();
    const msgId = `te-${Date.now()}`;
    connRef.current.send(
      $msg({ to: activeContact.jid, type: 'chat', id: msgId }).c('body').t(body)
    );
    const newMsg: Message = {
      id: msgId, fromJid: myJidRef.current, body, timestamp: new Date(), outgoing: true,
    };
    const key = bareJid(activeContact.jid);
    setMessages(prev => ({ ...prev, [key]: [...(prev[key] || []), newMsg] }));
    setInputText('');
    saveMessageToDB(newMsg, key.split('@')[0], true);
  };

  const sendFile = async (file: File) => {
    if (!activeContact || !token) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('to', bareJid(activeContact.jid).split('@')[0]);
    const msgId = `te-${Date.now()}`;
    formData.append('id', msgId);
    try {
      const r = await apiCall(ENDPOINTS.CHAT.UPLOAD, { method: 'POST', body: formData }, token);
      if (!r.ok) return;
      const data = await r.json();
      const newMsg: Message = {
        id:             data.id,
        fromJid:        myJidRef.current,
        body:           '',
        timestamp:      new Date(),
        outgoing:       true,
        attachmentUrl:  data.url,
        attachmentName: data.name,
        attachmentMime: data.mime,
      };
      const key = bareJid(activeContact.jid);
      setMessages(prev => ({ ...prev, [key]: [...(prev[key] || []), newMsg] }));
      // Notify recipient via XMPP so they can reload history
      if (connRef.current) {
        connRef.current.send(
          $msg({ to: activeContact.jid, type: 'chat', id: data.id })
            .c('body').t(`📎 ${data.name}`)
        );
      }
    } catch { /* silent */ }
  };

  // ─── Render helpers ───────────────────────────────────────────────────

  const sortedContacts = [...contacts].sort(
    (a, b) => PRESENCE_ORDER[a.presence] - PRESENCE_ORDER[b.presence]
  );

  const activeMessages = activeContact
    ? (messages[bareJid(activeContact.jid)] || [])
    : [];

  // ─── Roster view ──────────────────────────────────────────────────────

  if (!activeContact) {
    return (
      <div className="flex flex-col h-full">
        {/* Connection / availability status bar */}
        <div className="px-3 py-1.5 border-b border-base-300 bg-base-200 flex items-center gap-2 flex-shrink-0">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
            connStatus !== 'connected'   ? (connStatus === 'connecting' ? 'bg-warning animate-pulse' : 'bg-error') :
            xmppStatus === 'online'      ? 'bg-success' :
            xmppStatus === 'away'        ? 'bg-warning' :
            xmppStatus === 'dnd'         ? 'bg-error' : 'bg-base-300'
          }`} />
          <span className="text-xs text-base-content/60 flex-1">
            {connStatus !== 'connected'
              ? (connStatus === 'connecting' ? 'Connecting…' : connStatus === 'error' ? 'Connection failed' : 'Disconnected')
              : xmppStatus === 'online'   ? 'Online'
              : xmppStatus === 'away'     ? 'Away'
              : xmppStatus === 'dnd'      ? 'Do Not Disturb'
              : 'Appear Offline'}
          </span>
          {(connStatus === 'error' || connStatus === 'disconnected') && (
            <button className="btn btn-ghost btn-xs btn-circle" onClick={connect} title="Reconnect">
              <FiRefreshCw className="text-xs" />
            </button>
          )}
        </div>

        {/* Staff list */}
        <div className="flex-1 overflow-y-auto">
          {connStatus === 'connecting' && (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-base-content/40">
              <span className="loading loading-spinner loading-md" />
              <span className="text-xs">Connecting…</span>
            </div>
          )}

          {connStatus === 'error' && (
            <div className="flex flex-col items-center justify-center h-full gap-3 px-4 text-center text-base-content/40">
              <FiWifiOff className="text-2xl" />
              <p className="text-xs">Could not connect to chat server</p>
              <button className="btn btn-xs btn-primary" onClick={connect}>Retry</button>
            </div>
          )}

          {connStatus === 'connected' && sortedContacts.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-base-content/40 text-center px-4">
              <FiWifi className="text-2xl mb-2 opacity-40" />
              <p className="text-xs">No staff contacts yet.<br />They'll appear here as they come online.</p>
            </div>
          )}

          {connStatus === 'connected' && sortedContacts.map(contact => {
            const key = bareJid(contact.jid);
            const unreadCount = unread[key] || 0;
            return (
              <button
                key={contact.jid}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-base-200 transition-colors border-b border-base-300 last:border-0 text-left"
                onClick={() => openChat(contact)}
              >
                {/* Avatar with presence dot */}
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                    {contact.name.charAt(0).toUpperCase()}
                  </div>
                  <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-base-100 ${PRESENCE_DOT[contact.presence]}`} />
                </div>
                {/* Name + status */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{contact.name}</div>
                  <div className="text-xs text-base-content/50 capitalize">{contact.presence}</div>
                </div>
                {unreadCount > 0 && (
                  <span className="badge badge-sm badge-primary flex-shrink-0">{unreadCount}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Drag-and-drop ────────────────────────────────────────────────────

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) sendFile(file);
  };

  // ─── Chat view ────────────────────────────────────────────────────────

  return (
    <>
    <div
      className="flex flex-col h-full relative"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Drop overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-20 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex flex-col items-center justify-center gap-2 pointer-events-none">
          <FiPaperclip className="text-3xl text-primary" />
          <span className="text-sm font-medium text-primary">Drop to send file</span>
        </div>
      )}
      {/* Chat header */}
      <div className="px-2 py-2 border-b border-base-300 bg-base-200 flex items-center gap-2 flex-shrink-0">
        <button
          className="btn btn-ghost btn-xs btn-circle"
          onClick={() => setActiveContact(null)}
          title="Back to staff list"
        >
          <FiArrowLeft />
        </button>
        <div className="relative flex-shrink-0">
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
            {activeContact.name.charAt(0).toUpperCase()}
          </div>
          <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-base-200 ${PRESENCE_DOT[activeContact.presence]}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{activeContact.name}</div>
          <div className="text-xs text-base-content/50 capitalize">{activeContact.presence}</div>
        </div>
      </div>

      {/* Message thread */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {activeMessages.length === 0 && (
          <div className="flex items-center justify-center h-full text-base-content/30 text-xs">
            No messages yet — say hello!
          </div>
        )}
        {activeMessages.map(msg => {
          const openViewer = () => {
            if (msg.attachmentUrl)
              setViewer({ url: msg.attachmentUrl, name: msg.attachmentName || 'Attachment', mime: msg.attachmentMime || 'application/octet-stream' });
          };
          const openCtxMenu = (e: React.MouseEvent) => {
            if (!msg.attachmentUrl) return;
            e.preventDefault();
            setCtxMenu({ x: e.clientX, y: e.clientY, url: msg.attachmentUrl, name: msg.attachmentName || 'Attachment', mime: msg.attachmentMime || 'application/octet-stream' });
          };
          return (
            <div key={msg.id} className={`flex ${msg.outgoing ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-snug ${
                msg.outgoing
                  ? 'bg-primary text-primary-content rounded-br-sm'
                  : 'bg-base-200 text-base-content rounded-bl-sm'
              }`}>
                {msg.attachmentUrl && msg.attachmentMime?.startsWith('image/') ? (
                  <AuthImage
                    src={msg.attachmentUrl}
                    alt={msg.attachmentName}
                    className="max-w-full rounded-lg max-h-48 object-contain cursor-zoom-in"
                    onClick={openViewer}
                    onContextMenu={openCtxMenu}
                  />
                ) : msg.attachmentUrl ? (
                  <button
                    className={`flex items-center gap-2 underline text-left ${msg.outgoing ? 'text-primary-content' : 'text-primary'}`}
                    onClick={openViewer}
                    onContextMenu={openCtxMenu}
                  >
                    <FiPaperclip className="flex-shrink-0" />
                    <span className="truncate">{msg.attachmentName || 'Attachment'}</span>
                  </button>
                ) : (
                  <p className="break-words whitespace-pre-wrap">{msg.body}</p>
                )}
                <p className={`text-[10px] mt-1 ${msg.outgoing ? 'text-primary-content/60 text-right' : 'text-base-content/40'}`}>
                  {msg.timestamp.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Message input */}
      <div className="px-2 py-2 border-t border-base-300 flex-shrink-0 flex gap-2 items-end">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/jpeg,image/png,image/gif,image/webp,image/bmp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) sendFile(file);
            e.target.value = '';
          }}
        />
        <button
          className="btn btn-ghost btn-sm btn-square flex-shrink-0"
          onClick={() => fileInputRef.current?.click()}
          disabled={connStatus !== 'connected'}
          title="Attach file"
        >
          <FiPaperclip />
        </button>
        <textarea
          className="textarea textarea-bordered textarea-sm flex-1 resize-none text-sm leading-snug"
          placeholder={`Message ${activeContact.name}…`}
          value={inputText}
          rows={1}
          style={{ minHeight: '2.25rem', maxHeight: '6rem' }}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />
        <button
          className="btn btn-primary btn-sm btn-square flex-shrink-0"
          onClick={sendMessage}
          disabled={!inputText.trim() || connStatus !== 'connected'}
          title="Send (Enter)"
        >
          <FiSend />
        </button>
      </div>
    </div>

    {/* Media lightbox */}

    {viewer && (
      <ChatMediaViewer
        url={viewer.url}
        name={viewer.name}
        mime={viewer.mime}
        contactUsername={activeContact ? bareJid(activeContact.jid).split('@')[0] : ''}
        onClose={() => setViewer(null)}
        onAddToPatientFile={(url, name, mime) => {
          // TODO: wire to patient file selector
          alert(`"Add to Patient File" coming soon.\n\nFile: ${name}`);
          setViewer(null);
        }}
        onImageSaved={(newUrl, newName) => {
          setViewer(v => v ? { ...v, url: newUrl, name: newName } : null);
        }}
      />
    )}

    {/* Right-click context menu */}
    {ctxMenu && (
      <ul
        className="menu menu-sm bg-base-100 rounded-box shadow-xl border border-base-300 fixed z-50 w-52"
        style={{ top: ctxMenu.y, left: ctxMenu.x }}
        onClick={e => e.stopPropagation()}
      >
        <li>
          <button
            className="flex items-center gap-2 text-success font-medium"
            onClick={() => {
              // TODO: wire to patient file selector
              alert(`"Add to Patient File" coming soon.\n\nFile: ${ctxMenu.name}`);
              setCtxMenu(null);
            }}
          >
            Add to Patient File
          </button>
        </li>
        <li>
          <button
            onClick={() => { setViewer({ url: ctxMenu.url, name: ctxMenu.name, mime: ctxMenu.mime }); setCtxMenu(null); }}
          >
            View
          </button>
        </li>
        <li>
          <a href={ctxMenu.url} download={ctxMenu.name} onClick={() => setCtxMenu(null)}>
            Download
          </a>
        </li>
      </ul>
    )}
    </>
  );
};

export default ClinicalChatPanel;
